import * as React from 'react'
import { Outlet, useOutletContext, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import accessGroups from 'modules/core/store/collections/accessGroups'
import users from 'modules/core/store/collections/users'
import AccessGroup from 'modules/core/store/models/AccessGroup'

import { isManager } from 'modules/employees/lib/utils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import Participants from 'modules/surveys/components/Participants/Participants'
import SurveyShowHeader from 'modules/surveys/components/Show/SurveyShowHeader'
import SurveyShowPageTabs from 'modules/surveys/components/Show/SurveyShowPageTabs'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import formAccessGroupMemberships from 'modules/surveys/store/collections/formAccessGroupMemberships'
import type Form from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

import { SurveyParticipantsContext } from './hooks'

export default function ParticipantsPage() {
  const { employee } = useCurrentSession()
  const { id } = useParams()
  const { survey, hasSurveyV2, canEditSurvey, loading } = useSurveyShowContext()
  const { form } = useOutletContext<{ form: Form }>()
  const surveyModel = hasSurveyV2 ? (survey as Survey) : (form as Form)
  const [accessGroup, setAccessGroup] = React.useState<AccessGroup>()
  const { updateCanBeStarted } = useSurveyShowContext()

  const canSeeInfo =
    surveyModel?.attributes.visible_to_managers &&
    (isManager(employee) ||
      (users.current.employee
        ? userIsTeamLead(teams, memberships, users.current.employee.id)
        : false))

  const fetchRespondents = async () => {
    if ((!form && !hasSurveyV2) || (!survey && hasSurveyV2)) return

    const result =
      hasSurveyV2 && survey ? await survey.fetchRespondents() : await form.fetchRespondents()
    const accessGroup = new AccessGroup(result)
    accessGroups.add([accessGroup])

    await formAccessGroupMemberships.fetch({
      data: {
        form_id: id,
      },
    })
    setAccessGroup(accessGroup)

    await formAccessGroupMemberships.fetch({
      data: {
        access_group_id: accessGroup?.attributes.id,
        form_id: id,
      },
    })
    await updateCanBeStarted()
  }

  const { loading: loadingRespondents } = useUnsafeAsyncData(async () => fetchRespondents(), [id])

  if (!survey) {
    return null
  }

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading || loadingRespondents}
      error={null}
      authorized={employee?.hasRegularAccess && (canEditSurvey || canSeeInfo)}
    >
      <Box gap="s24">
        <SurveyShowHeader survey={survey} canEditSurvey={canEditSurvey} />
        <SurveyShowPageTabs form={survey} canEditSurvey={canEditSurvey} />
      </Box>
      <SurveyParticipantsContext.Provider
        value={{
          accessGroup,
          fetchRespondents,
        }}
      >
        <Participants
          form={surveyModel}
          onSubmit={fetchRespondents}
          canEditSurvey={canEditSurvey}
        />
        {!loading && <Outlet context={{ form, survey, accessGroup }} />}
      </SurveyParticipantsContext.Provider>
    </ResponsivePage.Body>
  )
}
